import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Download, FileText, Star } from "lucide-react";
import { cn } from "@/utils/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function TicketReports() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("backlog");
  const { data: stats, isLoading } = trpc.tickets.ticketDashboard.getStats.useQuery();
  const { data: workload = [] } = trpc.tickets.ticketDashboard.getAgentWorkload.useQuery();

  const TABS = [
    { key: "backlog", label: t("tickets.backlog") },
    { key: "sla", label: t("tickets.slaCompliance") },
    { key: "workload", label: t("tickets.agentWorkload") },
    { key: "csat", label: t("tickets.csat") },
  ] as const;

  if (isLoading) return <div className="flex-1 flex justify-center items-center"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>;

  return (
    <div className="flex-1 flex flex-col">
      {/* Page header */}
      <div className="px-6 py-4 border-b border-[#E0E0E0] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
            <FileText className="h-5 w-5 text-[#5B2C93]" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-[#2C2C2C]">{t("tickets.ticketReports")}</h1>
            <p className="text-sm text-[#6B6B6B]">{t("tickets.operationalReports")}</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => toast.info(t("tickets.exportComingSoon"))} className="gap-2 border-[#E0E0E0]">
          <Download className="h-4 w-4" />{t("tickets.export")}
        </Button>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4 pb-3 border-b border-[#E0E0E0]">
        <div className="flex gap-1 bg-[#F5F3FF] p-1 rounded-lg w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                tab === t.key
                  ? "bg-white text-[#5B2C93] shadow-sm ring-1 ring-[#E8DCF5]"
                  : "text-[#6B6B6B] hover:text-[#5B2C93]",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-5">
        {tab === "backlog" && (
          <div className="space-y-5">
            {/* KPI row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border border-[#E0E0E0] p-5 text-center">
                <p className="text-3xl font-bold text-[#5B2C93]">{stats?.openCount ?? 0}</p>
                <p className="text-sm text-[#6B6B6B] mt-1">{t("tickets.openTickets")}</p>
              </div>
              <div className="rounded-lg border border-[#E0E0E0] p-5 text-center">
                <p className="text-3xl font-bold text-[#D97706]">{stats?.mttr ?? 0}h</p>
                <p className="text-sm text-[#6B6B6B] mt-1">{t("tickets.meanTimeToResolve")}</p>
              </div>
              <div className="rounded-lg border border-[#E0E0E0] p-5 text-center">
                <p className="text-3xl font-bold text-[#0D9488]">{(stats?.volumeByDept ?? []).length}</p>
                <p className="text-sm text-[#6B6B6B] mt-1">{t("tickets.activeQueues")}</p>
              </div>
            </div>
            {/* Volume by Queue */}
            {(stats?.volumeByDept ?? []).length > 0 && (
              <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
                <div className="px-6 py-3.5 border-b border-[#E0E0E0] bg-[#FAFAFA]">
                  <h3 className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wide">{t("tickets.volumeByQueue")}</h3>
                </div>
                <div>
                  {(stats?.volumeByDept ?? []).map((d: any, i: number) => (
                    <div key={i} className="flex items-center justify-between px-6 py-3 border-b border-[#F0F0F0] last:border-0 hover:bg-[#F5F8FF]">
                      <span className="text-sm font-medium text-[#2C2C2C]">{d.queueName ?? t("tickets.unassigned")}</span>
                      <Badge variant="outline" className="bg-[#E8DCF5] text-[#5B2C93] border-[#E8DCF5]">{d.count} {t("tickets.tickets")}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "sla" && (
          <div className="space-y-5">
            <div className="rounded-lg border border-[#E0E0E0] p-8 text-center">
              <p className={cn(
                "text-6xl font-bold",
                (stats?.slaCompliance ?? 0) >= 80 ? "text-[#0D9488]" : (stats?.slaCompliance ?? 0) >= 50 ? "text-[#D97706]" : "text-[#DC2626]",
              )}>{stats?.slaCompliance ?? 0}%</p>
              <p className="text-sm text-[#6B6B6B] mt-2">{t("tickets.overallSlaCompliance")}</p>
              <div className="mt-4 w-full bg-[#F0F0F0] rounded-full h-3 max-w-md mx-auto">
                <div
                  className={cn(
                    "h-3 rounded-full transition-all",
                    (stats?.slaCompliance ?? 0) >= 80 ? "bg-[#0D9488]" : (stats?.slaCompliance ?? 0) >= 50 ? "bg-[#D97706]" : "bg-[#DC2626]",
                  )}
                  style={{ width: `${Math.min(stats?.slaCompliance ?? 0, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {tab === "workload" && (
          <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
            <div className="px-6 py-3.5 border-b border-[#E0E0E0] bg-[#FAFAFA]">
              <h3 className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wide">{t("tickets.agentWorkload")}</h3>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E0E0E0] bg-[#FAFAFA]">
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">{t("tickets.agent")}</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">{t("tickets.openTicketsLabel")}</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">{t("tickets.avgAgeHours")}</th>
              </tr></thead>
              <tbody>
                {workload.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-12 text-[#9CA3AF]">{t("tickets.noDataAvailable")}</td></tr>
                ) : (
                  workload.map((w: any) => (
                    <tr key={w.assigneeId} className="border-b border-[#F0F0F0] hover:bg-[#F5F8FF]">
                      <td className="px-6 py-3 font-medium text-[#2C2C2C]">{w.assigneeName ?? t("tickets.unknown")}</td>
                      <td className="px-6 py-3 text-[#6B6B6B]">{w.openCount}</td>
                      <td className="px-6 py-3 text-[#6B6B6B]">{Math.round(w.avgAge ?? 0)}h</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "csat" && (
          <div className="rounded-lg border border-[#E0E0E0] p-8 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#FFF4E5] mb-4">
              <Star className="h-8 w-8 text-[#D97706]" />
            </div>
            <p className="text-6xl font-bold text-[#2C2C2C]">{stats?.csatAvg || "—"}</p>
            <p className="text-sm text-[#6B6B6B] mt-2">{t("tickets.avgCsatScore")}</p>
            {stats?.csatAvg && (
              <div className="flex items-center justify-center gap-1 mt-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={cn(
                      "h-6 w-6",
                      s <= Math.round(Number(stats.csatAvg) || 0) ? "text-[#D97706] fill-[#D97706]" : "text-[#E0E0E0]",
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
